import {
    Component,
    State,
    Prop,
    Host,
    Element,
    Listen,
    h,
    Watch,
} from "@stencil/core";
import { waitForHydrated } from "@trumpf/ux.utils";
import api from "../../utils/api";
import { dispatchEvent } from "../../utils/dispatchEvent";
import { linkWrapperIgnoreAttribute } from "../link-wrapper/attributes";
import { scrollToTarget } from "../../utils/scrollToTarget";
import { createStaticFilter } from "../../utils/staticFilter/createStaticFilter";

import { SuccessStoriesResponse } from "../../../scripts/mock-generators/success-stories";

@Component({
    tag: "ux-iws-data-controller-success-stories",
    styleUrl: "data-controller-success-stories.scss",
})
export class DataControllerSuccessStories {
    private baseClass = "ux-iws-data-controller-success-stories";
    private teaserFilteredAttribute = "data-ux-success-story-hidden";
    private checkboxTag = "ux-checkbox-list-item";
    private resultsContainer: HTMLElement = null;
    private filterElement: HTMLUxFilterElement = null;

    @Element() element: HTMLUxIwsDataControllerSuccessStoriesElement;

    /** the url to which the request will be made for the results */
    @Prop() src: string = null;
    /** an optional text that indicated the loading state */
    @Prop() loadingText: string = "";
    /** the css selector of the ux-filter element that does the results filtering */
    @Prop() filterSelector: string = null;
    /** the css selector of the element that contains the number of the results */
    @Prop() counterSelector: string = null;
    /** optional text that can be shown to a user in case of an error */
    @Prop() errorText: string = "";
    /** optional form data element to be passed in the POST request */
    @Prop() formDataAttribute = null;
    /** filter attribute identifier for the boxes and tags */
    @Prop() filterAttributeIdentifier: string = "success-story";
    /** if set, the component expects to have already a set of filterable results in the results container slot */
    @Prop() synchronous: boolean = false;

    private tagFilterAttribute = null;
    private updateFilters: () => void = () => {};

    @State() error: boolean = false;
    @State() isLoading: boolean = !this.synchronous ? true : false;

    @Watch("error")
    hideLoadingBar() {
        if (this.error === true) this.isLoading = false;
    }

    async componentWillLoad() {
        this.tagFilterAttribute = `data-ux-${this.filterAttributeIdentifier}-tag`;

        this.resultsContainer = this.element.querySelector(
            "*[slot='results-container']"
        );
        this.filterElement = document.querySelector(this.filterSelector);
        const { store, update } = createStaticFilter({
            counterSelector: this.counterSelector,
            filterElement: this.filterElement,
            itemFilterAttribute: `data-ux-${this.filterAttributeIdentifier}-filter`,
            itemsContainerElement: this.resultsContainer,
            itemHiddenAttribute: this.teaserFilteredAttribute,
        });
        this.updateFilters = update;
        store.onChange("visibleItemsCount", () => {
            dispatchEvent("resize");
        });
        if (!this.resultsContainer) {
            this.error = true;
            return;
        }
        if (!this.synchronous) {
            this.getDataContent();
        }
    }

    private getDataContent = async () => {
        try {
            if (isMockUrl(this.src))
                await new Promise((resolve) => setTimeout(resolve, 2000));
            let response: SuccessStoriesResponse;
            if (isMockUrl(this.src))
                response = await api<SuccessStoriesResponse>(this.src);
            else {
                let uidList = "";
                let item: { uid: number };
                //@ts-expect-error --> global variable is (should be) present
                const resultsList = window.ResultLists;
                if (!resultsList) return;
                for (let i = 0; i < resultsList.length; i++) {
                    item = resultsList[i];
                    uidList += (uidList.length ? "," : "") + item.uid;
                }
                const formData = new FormData();
                const formDataAttribute =
                    this.formDataAttribute === null
                        ? "tx_trumpfspecials_resultlistcustomersuccessstories[uidList]"
                        : this.formDataAttribute;
                formData.append(formDataAttribute, uidList);
                response = await api<SuccessStoriesResponse>(this.src, {
                    method: "POST",
                    body: formData,
                });
            }
            if (response.status === "success") {
                if (
                    response?.data?.elements &&
                    response.data.elements.length > 0
                ) {
                    const htmlArray = response.data.elements.map((element) =>
                        typeof element.html === "string" ? element.html : ``
                    );
                    this.resultsContainer.insertAdjacentHTML(
                        "beforeend",
                        htmlArray.join("")
                    );
                    this.isLoading = false;
                    this.updateFilters();
                    const lastTeaser = this.element.querySelector(
                        "ux-iws-teaser-image:last-of-type"
                    ) as HTMLElement;
                    if (lastTeaser) {
                        waitForHydrated(lastTeaser, () => {
                            dispatchEvent("resize");
                        });
                    }
                } else {
                    this.error = true;
                }
            } else if (response.status === "invalid") {
                this.error = true;
            }
        } catch {
            this.error = true;
        }
    };

    @Listen("click", { target: "window" })
    tagFilterListener(e: Event) {
        const eTarget = e.target as HTMLElement;
        if (!eTarget.hasAttribute(linkWrapperIgnoreAttribute)) return;
        if (!eTarget.hasAttribute(this.tagFilterAttribute)) return;
        const tagFilterValue = eTarget.getAttribute(this.tagFilterAttribute);
        const filterCheckbox = this.filterElement.querySelector(
            `${this.checkboxTag}[value="${tagFilterValue}"] input`
        ) as HTMLInputElement;
        if (!filterCheckbox || filterCheckbox?.checked) return;
        else filterCheckbox.click();
        scrollToTarget({
            target: this.filterElement,
            additionalOffsetTop: 40,
            smooth: true,
        });
    }

    render() {
        return (
            <Host class={this.baseClass}>
                <style>
                    {`
                        .ux-filter${this.filterSelector} .ux-checkbox-list-item[disabled="true"] {
                            display: none;
                        }
                    `}
                </style>
                <div>
                    <div
                        style={
                            this.isLoading
                                ? {
                                      visibility: "hidden",
                                  }
                                : null
                        }
                    >
                        <slot name="counter" />
                    </div>
                </div>
                {!this.isLoading && this.error && (
                    <div class="ux-u-text-align--center">
                        {this.errorText !== "" && (
                            <div class="ux-u-mt-s">
                                <span class="text-results">
                                    {this.errorText}
                                </span>
                            </div>
                        )}
                        <ux-icon
                            name="close"
                            size-s="60"
                            class="ux-color-error ux-u-mt-s"
                        ></ux-icon>
                        <div style={{ height: "40vh" }}></div>
                    </div>
                )}
                <slot name="results-container"></slot>
                {this.isLoading && !this.error && (
                    <div class="ux-u-text-align--center">
                        {this.loadingText !== "" && (
                            <div class="ux-u-mx-s">
                                <span class="text-results">
                                    {this.loadingText}
                                </span>
                            </div>
                        )}
                        <div class="ux-u-mx-s">
                            <ux-loader variant="spinner"></ux-loader>
                        </div>
                        <div style={{ height: "40vh" }}></div>
                    </div>
                )}
            </Host>
        );
    }
}

function isMockUrl(url: string) {
    return (
        url.indexOf("/mock-data/") > -1 ||
        url.indexOf("/generated-mock-data/") > -1
    );
}
